import { stripTrackingTags } from '../../lib/agentState';
import MarkdownRenderer from '../MarkdownRenderer';
import AgentAvatar from '../AgentAvatar';
import ChatOptionWidget, { parseAgentOptions, type AgentOption } from './ChatOptionWidget';
import { useMemo } from 'react';
import { stripRegenerateMarker } from '@blueprint/shared';

interface ChatMessageProps {
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  streaming?: boolean;
  agentType?: string;
  /** Callback to send a message on behalf of the user (for option widgets) */
  onSendMessage?: (content: string) => void;
  /** Whether the message is from a historical/read-only session */
  readOnly?: boolean;
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

/** Parse "[Attached: file1.pdf, file2.png]" appended to user messages. */
function parseAttachments(raw: string): { text: string; attachments: string[] } {
  const match = raw.match(/^([\s\S]*?)\s*\[Attached: (.+?)\]$/);
  if (!match) return { text: raw, attachments: [] };
  return { text: match[1], attachments: match[2].split(', ') };
}

export default function ChatMessage({ role, content, createdAt, streaming, agentType = 'system', onSendMessage, readOnly }: ChatMessageProps) {
  // Extract AGENT_OPTIONS from raw content BEFORE stripping all HTML comments
  const agentOptions: AgentOption[] = useMemo(() => {
    if (role !== 'assistant' || streaming) return [];
    return parseAgentOptions(content);
  }, [role, content, streaming]);

  if (role === 'system') {
    return (
      <div className="flex justify-center my-2">
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }} className="italic">{content}</p>
      </div>
    );
  }

  const isUser = role === 'user';
  const rawContent = isUser ? stripRegenerateMarker(content) : stripTrackingTags(content);
  const { text: displayContent, attachments } = isUser
    ? parseAttachments(rawContent)
    : { text: rawContent, attachments: [] };

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Agent avatar — only for assistant messages */}
      {!isUser && (
        <div className="flex-shrink-0 self-end">
          <AgentAvatar agentType={agentType} size={32} />
        </div>
      )}

      {/* Bubble */}
      <div className={`max-w-[80%] flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
        <div
          style={
            isUser
              ? {
                  background: 'var(--primary)',
                  color: 'white',
                  borderRadius: '12px 12px 4px 12px',
                  padding: 'var(--space-4)',
                  boxShadow: 'var(--shadow-sm)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 14,
                  lineHeight: 1.6,
                }
              : {
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  borderRadius: '12px 12px 12px 4px',
                  padding: 'var(--space-4)',
                  boxShadow: 'var(--shadow-sm)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 14,
                  lineHeight: 1.6,
                }
          }
        >
          {isUser ? (
            <>
              <p className="whitespace-pre-wrap">{displayContent}</p>
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {attachments.map((name, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                      style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                    >
                      📎 {name}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
              <MarkdownRenderer
                disableMermaid={streaming}
                onRegenerateDiagram={!readOnly && !streaming ? onSendMessage : undefined}
              >
                {displayContent}
              </MarkdownRenderer>
              {streaming && (
                <span className="inline-block w-2 h-4 animate-pulse align-middle ml-0.5" style={{ background: 'var(--primary)', opacity: 0.6, borderRadius: 2 }} />
              )}
              {agentOptions.length > 0 && onSendMessage && (
                <div style={{ marginTop: 8 }}>
                  {agentOptions.map((opt, i) => (
                    <ChatOptionWidget
                      key={`${opt.question ?? ''}-${i}`}
                      option={opt}
                      onSend={onSendMessage}
                      disabled={readOnly}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', paddingLeft: 4, paddingRight: 4 }}>
          {formatTime(createdAt)}
        </span>
      </div>
    </div>
  );
}
